/**
 * Meta (Facebook) Pixel — consent-gated loader + event helpers.
 *
 * Unlike GA4 (loaded unconditionally in index.html), the Pixel is a *marketing*
 * tracker, so we only inject it once the visitor grants MARKETING consent. Every
 * helper here self-gates on that consent, so callers never need to check.
 *
 * CSP note: fbevents.js loads from connect.facebook.net and beacons to
 * facebook.com — the site CSP must allow those (see docs). Without the CSP
 * change the script is silently blocked (same class of bug as the review flyer).
 */

const PIXEL_ID = "2284664965633402";

/** True only when the visitor accepted MARKETING cookies. */
function marketingConsentGranted() {
  try {
    const raw = localStorage.getItem("fg_cookie_consent");
    return raw ? JSON.parse(raw)?.marketing === true : false;
  } catch {
    return false;
  }
}

/** Inject the fbq base snippet exactly once, then init + fire the first PageView. */
function injectPixel() {
  if (typeof window === "undefined" || window.fbq) return;
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", PIXEL_ID);
  window.fbq("track", "PageView");
}

/** Call on app load and whenever consent changes — loads the Pixel iff consented. */
export function initMetaPixelIfConsented() {
  if (marketingConsentGranted()) injectPixel();
}

/** Fire a Meta standard event (e.g. "ViewContent", "Search", "Contact"). No-ops without consent/pixel. */
export function pixelTrack(event, params = {}) {
  if (marketingConsentGranted() && typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq("track", event, params);
  }
}

/** Fire a Meta *custom* event (non-standard name, e.g. "InstallBannerClick"). */
export function pixelTrackCustom(event, params = {}) {
  if (marketingConsentGranted() && typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq("trackCustom", event, params);
  }
}

/** SPA route-change PageView (the base snippet only fires PageView once, on load). */
export function pixelPageView() {
  pixelTrack("PageView");
}

/**
 * Bridge from a GA4 event name to the matching Meta Pixel event, so existing
 * trackEvent() call sites feed the Pixel without extra wiring. Self-gated.
 */
export function pixelFromGaEvent(name, params = {}) {
  switch (name) {
    case "app_banner_click":
      // Primary conversion for this awareness campaign: intent to install the app.
      pixelTrackCustom("InstallBannerClick", { platform: params?.platform });
      pixelTrack("Lead", { content_name: "android_app_install" });
      break;
    case "business_contact":
      pixelTrack("Contact", {
        content_name: params?.business_name,
        content_ids: params?.business_id ? [params.business_id] : undefined,
      });
      break;
    default:
      break;
  }
}
